import React, { useEffect, useRef, useState } from "react";
import "./movie.css";
import { useParams } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import { IconButton } from "@mui/material";
import Recommend from "../Recommend/Recommend";
import { db, auth } from "../../firebase";
import tmdbConfigs from "../api/tmdb.config";
import { doc, getDoc, getFirestore, setDoc, updateDoc } from "firebase/firestore";
import { Favorite, FavoriteBorder } from "@mui/icons-material";

const Movie = ({theme}) => {
    const { id } = useParams();
    const [currentMovieDetail, setMovieDetail] = useState(null);
    const [videos, setVideos] = useState([]);
    const [cast, setCast] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);
    const [userEmail, setUserEmail] = useState("");
    const [mediaType, setMediaType] = useState("");
    const iframeRef = useRef();

    useEffect(() => {
        fetchData();
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserEmail(user.email);
            } else {
                setUserEmail("");
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const checkFavoriteStatus = async () => {
            const userId = auth.currentUser?.uid;
            if (!userId) return;

            const userRef = doc(db, "userFavorite", userId);
            const docSnap = await getDoc(userRef);

            if (docSnap.exists()) {
                const favoritesData = docSnap.data().favorites || {};
                setIsFavorite(!!favoritesData[id]);
            } else {
                setIsFavorite(false);
            }
        };

        checkFavoriteStatus();
    }, [id]);

    const fetchData = () => {
        fetch(
            `https://api.themoviedb.org/3/movie/${id}?api_key=4e44d9029b1270a757cddc766a1bcb63&language=en-US&append_to_response=videos,credits`
        )
            .then((res) => res.json())
            .then((data) => {
                setMovieDetail(data);
                if (data?.videos?.results) {
                    setVideos(data.videos.results);
                }
                if (data?.credits?.cast) {
                    setCast(data.credits.cast);
                }
                if (data.release_date) {
                    setMediaType("movie");
                } else if (data.first_air_date) {
                    setMediaType("tv");
                }
            })
            .catch((error) => console.error("Error fetching movie details:", error));
    };

    const nextSlide = () => {
        setCurrentSlide((prevSlide) => (prevSlide === videos.length - 1 ? 0 : prevSlide + 1));
    };

    const prevSlide = () => {
        setCurrentSlide((prevSlide) => (prevSlide === 0 ? videos.length - 1 : prevSlide - 1));
    };

    useEffect(() => {
        if (videos.length > 0 && iframeRef.current) {
            const height = iframeRef.current.offsetWidth * 9 / 16 + "px";
            iframeRef.current.setAttribute("height", height);
        }
    }, [currentSlide, videos]);

    const renderGenres = () =>
        currentMovieDetail.genres.map((genre) => (
            <span key={genre.id} className="movie__genre">
                {genre.name}
            </span>
        ));

    const handleRemoveButtonClick = async () => {
        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("User not logged in");
                return;
            }
            const userId = user.uid;
            const favoritesRef = doc(getFirestore(), "userFavorite", userId);

            const favoritesDoc = await getDoc(favoritesRef);
            if (favoritesDoc.exists()) {
                const favoritesData = favoritesDoc.data().favorites || {};
                if (favoritesData[id]) {
                    delete favoritesData[id];
                    await updateDoc(favoritesRef, { favorites: favoritesData });
                } else {
                    console.error("Favorite document does not exist:", id);
                }
            } else {
                console.error("Favorites document does not exist for user:", userId);
            }
        } catch (error) {
            console.error("Error removing favorite from list: ", error);
        }
    };

    const toggleFavorite = async () => {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) {
                console.error("User is not authenticated");
                return;
            }

            const userRef = doc(db, "userFavorite", userId);
            const docSnap = await getDoc(userRef);

            let favoriteObject = {};
            if (currentMovieDetail) {
                const gen = currentMovieDetail.genres || [];

                favoriteObject = {
                    movieId: id,
                    mediaType: mediaType,
                    posterPath: tmdbConfigs.posterPath(currentMovieDetail.poster_path),
                    mediaTitle: currentMovieDetail.title,
                    mediaRate: currentMovieDetail.vote_average,
                    genres: gen,
                };
            } else {
                throw new Error("Movie detail not found");
            }

            if (docSnap.exists()) {
                const favoritesData = docSnap.data().favorites || {};

                if (isFavorite) {
                    // Remove the movie from favorites
                    handleRemoveButtonClick();
                } else {
                    // Add the movie to favorites
                    favoritesData[id] = favoriteObject;
                }

                // Update the user's favorites in Firestore
                await setDoc(userRef, { favorites: favoritesData }, { merge: true });
                setIsFavorite(!isFavorite);

                console.log(`Toggled favorite for mediaId: ${id} for user: ${userId}`);
            } else {
                // Create the user document if it doesn't exist and add the favorite
                await setDoc(userRef, { favorites: { [id]: favoriteObject } });
                setIsFavorite(true); // Assume the movie is now a favorite
                console.log("User document created and movie added to favorites.");
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };
    const responsive = {
        superLargeDesktop: {
            breakpoint: { max: 4000, min: 3000 },
            items: 5
        },
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 4
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 2
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1
        }
    };


    const updateViewedHistory = async () => {
        try {
            const historyRef = doc(db, "history", userEmail);
            const docSnap = await getDoc(historyRef);

            let historyData = {};
            if (docSnap.exists()) {
                historyData = docSnap.data();
            } else {
                historyData.movies = [];
            }

            const movieData = {
                movieId: id,
                mediaTitle: currentMovieDetail.title,
                posterPath: currentMovieDetail?.poster_path,
                genres: currentMovieDetail?.genres.map((genre) => genre.name),
                mediaRate: currentMovieDetail.vote_average,
                timestamps: [new Date()],
            };

            const existingMovieIndex = historyData.movies
                ? historyData.movies.findIndex((movie) => movie.movieId === id)
                : -1;

            if (existingMovieIndex !== -1) {
                if (!historyData.movies[existingMovieIndex].timestamps) {
                    historyData.movies[existingMovieIndex].timestamps = [];
                }

                historyData.movies[existingMovieIndex].timestamps.push(new Date());
            } else {
                if (!historyData.movies) {
                    historyData.movies = [movieData];
                } else {
                    historyData.movies.push(movieData);
                }
            }

            await setDoc(historyRef, historyData);

            console.log("Viewed history updated successfully.");
        } catch (error) {
            console.error("Error updating viewed history:", error);
        }
    };

    useEffect(() => {
        if (currentMovieDetail && userEmail) {
            updateViewedHistory();
        }
    }, [currentMovieDetail, userEmail]);



    return (
        <div className={`container ${theme}`}>
            <div className="row">
                <div className="col-12">
                    <div className="movie__intro">
                        <img
                            className="movie__backdrop img-fluid"
                            src={`https://image.tmdb.org/t/p/original${currentMovieDetail?.backdrop_path || ""}`}
                            alt="Backdrop"
                        />
                    </div>
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-md-4 col-12 mb-4 mb-md-0">
                    <div className="movie__detail">
                        <div className="movie__detailLeft">
                            <div className="movie__posterBox">
                                <img
                                    className="movie__poster img-fluid"
                                    src={`https://image.tmdb.org/t/p/original${currentMovieDetail?.poster_path || ""}`}
                                    alt="Poster"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-8 col-12">
                    <div className="movie__detailRight">
                        <div className="movie__detailRightTop">
                            <div className={`movie__name ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                                {currentMovieDetail?.original_title || ""}
                            </div>
                            <div className={`movie__tagline ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                                {currentMovieDetail?.tagline || ""}
                            </div>
                            <div className={`movie__rating ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                                {currentMovieDetail?.vote_average || ""}{" "}
                                <i className="fas fa-star" />
                                <span className="movie__voteCount">
                                    {currentMovieDetail ? `(${currentMovieDetail.vote_count} votes)` : ""}
                                </span>
                            </div>
                            <div className={`movie__runtime ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                                {currentMovieDetail ? `${currentMovieDetail.runtime} mins` : ""}
                            </div>
                            <div className={`movie__releaseDate ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                                {currentMovieDetail ? `Release date: ${currentMovieDetail.release_date}` : ""}
                            </div>
                            <div className={`movie__genres ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>
                                {currentMovieDetail?.genres ? renderGenres() : ""}
                            </div>
                            <div className="row">
                                <div className="col-12">
                                    <IconButton
                                        onClick={toggleFavorite}
                                        sx={{
                                            "&:hover": { backgroundColor: "transparent" },
                                            color: theme === "dark" ? (isFavorite ? "red" : "lightblue") : (isFavorite ? "red" : "black")
                                        }}
                                    >
                                        {isFavorite ? <Favorite /> : <FavoriteBorder />}
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                        <div className="movie__detailRightBottom mt-3" style={{ color: theme === "dark" ? "white" : "black" }}>
                            <div className="synopsisText">Synopsis</div>
                            <div>{currentMovieDetail?.overview || ""}</div>
                        </div>
                        <br/>   <br/>   <br/>
                    </div>
                </div>
            </div>
            <br/>   <br/>   <br/>                         <br/>   <br/>   <br/>


            <div className="row mt-4">
                <div className="col-12">
                    <div className="movie__heading" style={{ marginLeft: "3%", color: theme === "dark" ? "white" : "black" }}>Production companies</div>
                    <div className="movie__production" style={{ display: "flex", flexWrap: "wrap", gap: "10px", overflow: "hidden" }}>
                        {currentMovieDetail?.production_companies &&
                            currentMovieDetail.production_companies.map((company) =>
                                company.logo_path ? (
                                    <span className="productionCompanyImage" key={company.id} style={{ display: "flex", alignItems: "center", color: theme === "dark" ? "white" : "black" }}>
                                        <img
                                            className="movie__productionCompany img-fluid"
                                            src={`https://image.tmdb.org/t/p/original${company.logo_path}`}
                                            alt={company.name}
                                            style={{ maxWidth: "100px", height: "auto", marginRight: "5px" }}
                                        />
                                        <span>{company.name}</span>
                                    </span>
                                ) : null
                            )}
                    </div>
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-12">
                    <div className="movie__heading" style={{ marginLeft: "3%" }}>Video Trailers</div>
                    {videos.length > 0 && (
                        <div className="d-flex justify-content-center">
                            <div className="embed-responsive embed-responsive-16by9" style={{ width: '100%', maxWidth: '800px' }}>
                                <Carousel showArrows={true} showThumbs={false} showStatus={false} infiniteLoop={true}>
                                    {videos.map((video, index) => (
                                        <div key={index} style={{ display: "flex", justifyContent: "center" }}>
                                            <iframe
                                                key={video.key}
                                                src={tmdbConfigs.youtubePath(video.key)}
                                                className="embed-responsive-item"
                                                title={video.id}
                                                style={{ border: 0, width: '100%', height: '450px' }}
                                            ></iframe>
                                        </div>
                                    ))}
                                </Carousel>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-12">
                    <div className="movie__heading" style={{ marginLeft: "3%", color: theme === "dark" ? "white" : "black" }}>Cast</div>
                    <Carousel responsive={responsive} infinite={true} autoPlay={false} containerClass="movie__castContainer">
                        {cast.map((actor, index) => (
                            <div key={index} className="movie__castSlide p-2">
                                <img
                                    src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                                    alt={actor.name}
                                    className="movie__castImage small img-fluid"
                                />
                                <div className={`movie__castName ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{actor.name}</div>
                                <div className={`movie__castCharacter ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{actor.character}</div>
                            </div>
                        ))}
                    </Carousel>
                </div>
            </div>


            {/* Recommendation component goes here */}
             <Recommend movieId={id} />
        </div>
    );
};

export default Movie;
